import { format } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ReportExport } from "../ReportExport";
import { Skeleton } from "@/components/ui/skeleton";

interface BalanceSheetReportProps {
  reportData: any;
  isLoading?: boolean;
  name: string;
  onSave?: () => void;
}

export function BalanceSheetReport({ 
  reportData, 
  isLoading = false,
  name,
  onSave
}: BalanceSheetReportProps) {
  if (isLoading) {
    return <BalanceSheetSkeleton />;
  }

  if (!reportData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Balance Sheet</CardTitle>
          <CardDescription>No data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Card className="shadow-sm border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{name || "Balance Sheet"}</CardTitle>
            <CardDescription>
              As of {format(new Date(reportData.asOfDate), "MMMM d, yyyy")}
            </CardDescription>
          </div>
          <ReportExport 
            reportName={name || "Balance Sheet"} 
            reportData={reportData} 
            onSave={onSave}
          />
        </div>
      </CardHeader>
      
      <CardContent className="pt-4">
        <div className="space-y-6">
          {/* Assets Section */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Assets</h3>
            <Separator className="my-2" />
            
            <div className="space-y-2">
              {reportData.assets.accounts.map((account: any) => (
                <div key={account.id} className="flex justify-between items-center py-1">
                  <span className="text-sm text-slate-600">{account.name}</span>
                  <span className="text-sm font-medium">{formatCurrency(account.balance)}</span>
                </div>
              ))}
              
              <div className="flex justify-between items-center py-2 font-medium">
                <span>Total Assets</span>
                <span>{formatCurrency(reportData.assets.total)}</span>
              </div>
            </div>
          </div>
          
          {/* Liabilities Section */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Liabilities</h3>
            <Separator className="my-2" />
            
            <div className="space-y-2">
              {reportData.liabilities.accounts.map((account: any) => (
                <div key={account.id} className="flex justify-between items-center py-1">
                  <span className="text-sm text-slate-600">{account.name}</span>
                  <span className="text-sm font-medium">{formatCurrency(account.balance)}</span>
                </div>
              ))}
              
              <div className="flex justify-between items-center py-2 font-medium">
                <span>Total Liabilities</span>
                <span>{formatCurrency(reportData.liabilities.total)}</span>
              </div>
            </div>
          </div>
          
          {/* Equity Section */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Equity</h3>
            <Separator className="my-2" />
            
            <div className="space-y-2">
              {reportData.equity.accounts.map((account: any) => (
                <div key={account.id} className="flex justify-between items-center py-1">
                  <span className="text-sm text-slate-600">{account.name}</span>
                  <span className="text-sm font-medium">{formatCurrency(account.balance)}</span>
                </div>
              ))}
              
              <div className="flex justify-between items-center py-2 font-medium">
                <span>Total Equity</span>
                <span>{formatCurrency(reportData.equity.total)}</span>
              </div>
            </div>
          </div>
          
          {/* Total Liabilities & Equity */}
          <div className="pt-2">
            <Separator className="my-2" />
            <div className="flex justify-between items-center py-2 font-semibold text-lg">
              <span>Total Liabilities & Equity</span>
              <span>{formatCurrency(reportData.totalLiabilitiesAndEquity)}</span>
            </div>
          </div>
          
          {/* Summary Statistics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
            <Card className="bg-slate-50">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Total Assets</div>
                <div className="text-2xl font-bold">{formatCurrency(reportData.assets.total)}</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-50">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Total Liabilities</div>
                <div className="text-2xl font-bold">{formatCurrency(reportData.liabilities.total)}</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-50">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Total Equity</div>
                <div className="text-2xl font-bold">{formatCurrency(reportData.equity.total)}</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BalanceSheetSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48 mt-2" />
      </CardHeader>
      <CardContent className="pt-4 space-y-6">
        <div>
          <Skeleton className="h-6 w-40 mb-2" />
          <Separator className="my-2" />
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="flex justify-between items-center">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-5 w-28" />
            </div>
          </div>
        </div>
        
        <div>
          <Skeleton className="h-6 w-40 mb-2" />
          <Separator className="my-2" />
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="flex justify-between items-center">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-5 w-28" />
            </div>
          </div>
        </div>
        
        <div>
          <Skeleton className="h-6 w-40 mb-2" />
          <Separator className="my-2" />
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="flex justify-between items-center">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-5 w-28" />
            </div>
          </div>
        </div>
        
        <div>
          <Separator className="my-2" />
          <div className="flex justify-between items-center">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-6 w-32" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
          <Card className="bg-slate-50">
            <CardContent className="p-4">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </CardContent>
          </Card>
          <Card className="bg-slate-50">
            <CardContent className="p-4">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </CardContent>
          </Card>
          <Card className="bg-slate-50">
            <CardContent className="p-4">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
} 